/**
 * Light theme. Landing uses clean SaaS palette; app uses Polaroid accents.
 * backgroundPage: Figma-style cool off-white for landing
 */
export const colors = {
  // Page & backgrounds (landing: cool off-white)
  backgroundPage: '#F9F9F7',
  sectionBgAlt: '#F3F3F1',
  surface: '#FFFFFF',
  // App canvas (itinerary screens: warm cream)
  appBg: '#F5F0E8',

  // Peg board (day columns)
  pegboardBg: '#D4C4A8',
  pegboardHole: 'rgba(139, 119, 101, 0.4)',
  pegboardTimeline: '#E8DCC8',
  pegboardHover: '#F5F1EB',

  // Polaroid frame (place cards)
  polaroidFrame: '#FDFCFA',
  polaroidFrameAlt: '#F8F6F2',
  polaroidFrameBorder: '#E8E4DC',
  polaroidCaptionBg: '#FDFCFA',
  polaroidShadow: 'rgba(0, 0, 0, 0.12)',

  // Rainbow accent buttons (Polaroid brand)
  accentCerulean: '#00A3DA',
  accentCeruleanHover: '#33B5E9',
  // Cerulean tuned for text/links on light surfaces. The brand
  // accentCerulean (#00A3DA) is only ~3:1 on white and fails WCAG AA for
  // normal text; this darker shade clears 4.5:1. Use it for inline text
  // links, not for filled buttons (those carry their own white label).
  accentCeruleanInk: '#0077A8',
  accentGreen: '#50C878',
  accentGreenHover: '#34B85F',
  accentYellow: '#FDC800',
  accentYellowHover: '#FDD633',
  accentOrange: '#FF6B35',
  accentOrangeHover: '#F49D3D',
  accentCrimson: '#DC143C',
  accentCrimsonHover: '#EA4A44',

  // Polaroid rainbow (photo area backgrounds)
  polaroidRainbow: ['#00A3DA', '#50C878', '#FF6B35', '#DC143C'] as const,
  polaroidRainbowLight: ['#F0FAFF', '#EDFAF4', '#FFF4EF', '#FFF0F0'] as const,
  polaroidRainbowBorder: ['#7DCFED', '#8DDBB0', '#FFB49A', '#F08080'] as const,

  // Semantic
  surfacePressed: '#EDE8E0',
  borderDefault: '#D4D4D4',
  borderMuted: '#E0E0E0',
  borderLight: '#E5E5E5',
  textOnSurface: '#1A1A1A',
  textSubdued: '#6B7280',
  dropTargetHighlight: 'rgba(27, 165, 72, 0.2)',
  overlayDark: 'rgba(0,0,0,0.5)',
  overlayLight: 'rgba(0,0,0,0.08)',
  overlayScrim: 'rgba(0,0,0,0.35)',
  destructive: '#E41B13',
  success: '#4CAF50',

  // Text/icon on dark surfaces (header, overlays — always dark regardless of theme)
  textOnDark: '#FFFFFF',
  textOnDarkMuted: 'rgba(255,255,255,0.85)',
  textOnDarkSubdued: 'rgba(255,255,255,0.6)',
  iconOnDark: '#FFFFFF',

  /** Text colour for primary buttons whose bg is `$textOnSurface`.
   *  Inverts per theme (white in light, near-black in dark) so labels
   *  contrast with the inverted-from-page button surface. Use this
   *  instead of `$textOnDark` for any button that uses
   *  `bg: $textOnSurface` — `$textOnDark` is for genuinely-dark
   *  surfaces (photo overlays) and stays white in both themes. */
  textInverse: '#FFFFFF',

  // Controls on dark surfaces (header buttons, pills)
  overlayOnDark: 'rgba(255,255,255,0.2)',
  overlayOnDarkPressed: 'rgba(255,255,255,0.3)',
  borderOnDark: 'rgba(255,255,255,0.15)',

  // Card overlays
  overlayCardAction: 'rgba(255,255,255,0.9)',
  overlayGradient: 'rgba(0,0,0,0.72)',

  // Shadow
  shadowDefault: '#000000',

  // Time column
  timeColumnBg: '#E5DDD0',
  timeColumnText: '#5C5346',

  // New design (Polaroid Round-2) palette — fills gaps the legacy
  // tokens don't cover. ADR-0029 + ADR-0033 require all new-design
  // surfaces to read these instead of hex literals so dark mode +
  // rebrand work without per-component fixes.
  /** Page background on new-design canonical screens (different from
   *  the warm cream `appBg` legacy uses). */
  surfaceCanvas: '#F4F4F4',
  /** Pill / chip / muted-button background on new-design screens.
   *  Matches surfaceCanvas in light mode but stays slightly elevated
   *  in dark mode so chips feel tappable on dark surfaces. */
  surfaceChip: '#F4F4F4',
  /** Hairline divider between rows in field groups, edit panels. */
  dividerSubtle: '#F0F0F0',
  /** Disabled / placeholder swatch. */
  inkDisabled: '#C8C8C8',
  /** Inline-warning text (less alarming than $destructive). Used for
   *  validation errors that aren't destructive actions. */
  inkWarning: '#A03C00',
  /** Inline-success text. */
  inkSuccess: '#235A20',
  /** Soft warm orange chip background — error states, danger callouts. */
  surfaceWarning: '#FFE9DD',

  // --- Trip / day progress status (ADR-0088) -------------------------
  /** Past-trip progress paint — desaturated slate ("archived"). Pairs
   *  with accentOrange (current) and accentCerulean (upcoming) to colour
   *  the day progress bar by where the trip sits relative to today. */
  accentSlate: '#94A3B8',
  /** "Today" chip tint — soft green wash behind the calendar-today day
   *  in the expanded day picker. Pairs with accentGreen border/label. */
  surfaceToday: '#EDFAF4',

  // --- State token families (ADR-0072) -------------------------------
  // Every entry below must have a matching value in colorsDark.
  // theme/colors.test.ts enforces parity.

  /** Pointer-over surface tint (web). Slightly cooler than pressed. */
  surfaceHover: '#F1ECE3',
  /** Surface behind focused fields when a soft fill is desired. */
  surfaceFocus: '#EEF7FC',
  /** Disabled background for inputs / button surfaces. */
  surfaceDisabled: '#EFEFEF',
  /** Stronger error-state surface for callouts. */
  surfaceError: '#FFD9D4',
  /** Subtle error tint for inline error messages. */
  surfaceErrorSubtle: '#FFF1EF',
  /** Information / hint surface — soft cerulean. */
  surfaceInfo: '#E6F4FB',
  /** Brand-accent surface — used by branded empty states. */
  surfaceBrand: '#FDF4D6',
  /** Subtle brand-tint for chips, badges. */
  surfaceBrandSubtle: '#FFF9E5',

  /** Border for focus indicators — defaults to cerulean. */
  borderFocus: '#00A3DA',
  /** Border for error / invalid inputs. */
  borderError: '#E41B13',
  /** Border for disabled inputs / surfaces. */
  borderDisabled: '#D8D8D8',

  /** Disabled text colour. Paired with surfaceDisabled. */
  textDisabled: '#A0A0A0',
  /** Error text in form messages, inline warnings. */
  textError: '#C81100',
  /** Warning text — less alarming than textError. */
  textWarning: '#A03C00',
  /** Success text in form messages. */
  textSuccess: '#1F6F1A',

  /** Per-accent pressed pairs (ADR-0072). */
  accentCeruleanPressed: '#0086B3',
  accentGreenPressed: '#3FA862',
  accentYellowPressed: '#D9AB00',
  accentOrangePressed: '#E15722',
  accentCrimsonPressed: '#B30E2F',

  /** Numeric opacity tokens, used as multipliers on Tamagui's opacity prop. */
  pressOpacity: 0.85,
  hoverOpacity: 0.95,
  disabledOpacity: 0.45,

  /** Focus-ring outline (web). */
  focusRingColor: '#00A3DA',
  focusRingWidth: 2,
  focusRingOffset: 2,

  // --- Decoration paints (ADR-0072) ----------------------------------
  // `CloudDecoration` and `RoadRailDecoration` keep their multi-hue
  // ramps internally (theme-aware via useThemeColors); only the
  // diagonal hatch needs a single named paint.
  /** Diagonal hatch overlay stripe colour. */
  diagonalOverlayStripe: 'rgba(0, 0, 0, 0.14)',
} as const;

/**
 * Dark theme. Inverted backgrounds with Polaroid accent pops.
 * Polaroid cards stay white-ish so they still look like polaroids on dark pages.
 */
export const colorsDark = {
  backgroundPage: '#1A1A1A',
  sectionBgAlt: '#252525',
  surface: '#2D2D2D',
  appBg: '#1E1B18',

  pegboardBg: '#3D3630',
  pegboardHole: 'rgba(80, 70, 60, 0.5)',
  pegboardTimeline: '#4A433C',
  pegboardHover: '#3D3730',

  // Keep polaroid cards white-ish in dark mode (slightly softer than light theme)
  polaroidFrame: '#F0EDE8',
  polaroidFrameAlt: '#EBE8E3',
  polaroidFrameBorder: '#E0DCD6',
  polaroidCaptionBg: '#F0EDE8',
  polaroidShadow: 'rgba(0, 0, 0, 0.35)',

  accentCerulean: '#00A3DA',
  accentCeruleanHover: '#33B5E9',
  // On dark surfaces the brand cerulean already passes; a slightly
  // lighter shade keeps inline links comfortably above 4.5:1.
  accentCeruleanInk: '#33B5E9',
  accentGreen: '#50C878',
  accentGreenHover: '#34B85F',
  accentYellow: '#FDC800',
  accentYellowHover: '#FDD633',
  accentOrange: '#FF6B35',
  accentOrangeHover: '#F49D3D',
  accentCrimson: '#DC143C',
  accentCrimsonHover: '#EA4A44',

  polaroidRainbow: ['#00A3DA', '#50C878', '#FF6B35', '#DC143C'] as const,
  polaroidRainbowLight: ['#F0FAFF', '#EDFAF4', '#FFF4EF', '#FFF0F0'] as const,
  polaroidRainbowBorder: ['#7DCFED', '#8DDBB0', '#FFB49A', '#F08080'] as const,

  surfacePressed: '#3D3D3D',
  borderDefault: '#4A4A4A',
  borderMuted: '#404040',
  borderLight: '#333333',
  textOnSurface: '#F5F5F5',
  textSubdued: '#A0A0A0',
  dropTargetHighlight: 'rgba(27, 165, 72, 0.25)',
  overlayDark: 'rgba(0,0,0,0.55)',
  overlayLight: 'rgba(255,255,255,0.12)',
  overlayScrim: 'rgba(0,0,0,0.5)',
  destructive: '#FF4D4D',
  success: '#66BB6A',

  textOnDark: '#FFFFFF',
  textOnDarkMuted: 'rgba(255,255,255,0.85)',
  textOnDarkSubdued: 'rgba(255,255,255,0.6)',
  iconOnDark: '#FFFFFF',

  /** Inverse text — paired with `$textOnSurface`-bg buttons. Dark
   *  here so light-coloured `textOnSurface` buttons stay legible. */
  textInverse: '#0A0A0A',

  overlayOnDark: 'rgba(255,255,255,0.15)',
  overlayOnDarkPressed: 'rgba(255,255,255,0.25)',
  borderOnDark: 'rgba(255,255,255,0.1)',

  overlayCardAction: 'rgba(55,55,55,0.95)',
  overlayGradient: 'rgba(0,0,0,0.72)',

  shadowDefault: '#000000',

  timeColumnBg: '#35302A',
  timeColumnText: '#B8AFA5',

  // New design palette — dark variants.
  surfaceCanvas: '#1A1A1A',
  surfaceChip: '#3A3A3A',
  dividerSubtle: '#2A2A2A',
  inkDisabled: '#4A4A4A',
  inkWarning: '#FF8A65',
  inkSuccess: '#81C784',
  surfaceWarning: 'rgba(255, 138, 101, 0.18)',

  // --- Trip / day progress status (ADR-0088) -- dark theme -----------
  accentSlate: '#8B97A8',
  surfaceToday: 'rgba(80, 200, 120, 0.16)',

  // --- State token families (ADR-0072) -- dark theme -----------------

  surfaceHover: '#3A3A3A',
  surfaceFocus: 'rgba(0, 163, 218, 0.18)',
  surfaceDisabled: '#2A2A2A',
  surfaceError: 'rgba(255, 77, 77, 0.22)',
  surfaceErrorSubtle: 'rgba(255, 77, 77, 0.10)',
  surfaceInfo: 'rgba(0, 163, 218, 0.18)',
  surfaceBrand: 'rgba(253, 200, 0, 0.18)',
  surfaceBrandSubtle: 'rgba(253, 200, 0, 0.10)',

  borderFocus: '#33B5E9',
  borderError: '#FF4D4D',
  borderDisabled: '#3A3A3A',

  textDisabled: '#666666',
  textError: '#FF8A85',
  textWarning: '#FF8A65',
  textSuccess: '#81C784',

  accentCeruleanPressed: '#0086B3',
  accentGreenPressed: '#3FA862',
  accentYellowPressed: '#D9AB00',
  accentOrangePressed: '#E15722',
  accentCrimsonPressed: '#B30E2F',

  pressOpacity: 0.80,
  hoverOpacity: 0.92,
  disabledOpacity: 0.40,

  focusRingColor: '#33B5E9',
  focusRingWidth: 2,
  focusRingOffset: 2,

  // Decoration paints — dark.
  diagonalOverlayStripe: 'rgba(255, 255, 255, 0.10)',
} as const;

export type ThemeColors = typeof colors;
